import { Component, Input, OnInit } from '@angular/core';
import { ITreeOptions } from 'angular-tree-component';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Layer,
  LayerPermission,
  Permission,
  PermissionSubject,
  PermissionType,
  PermissionTypeItem,
  PermissionTypes,
  Project
} from '../../classes';
import { PermissionService } from '../../services';

const cloneDeep = require('lodash/cloneDeep');

@Component({
  selector: 'geo-permissions',
  templateUrl: 'permissions.component.html',
  styleUrls: ['./permissions.component.less']
})
export class PermissionsComponent implements OnInit {
  @Input() project: Project;

  permissionSubject: PermissionSubject;
  permissionType: PermissionTypeItem;

  autocomplete: PermissionSubject[] = [];
  permissionTypes: PermissionTypeItem[] = PermissionTypes;

  layerFilter = '';
  filteredLayers: Layer[] = [];
  showLayers = true;

  layerTreeOptions: ITreeOptions = {
    displayField: 'title',
    childrenField: 'sublayers'
  };

  constructor(private permissionService: PermissionService) {}

  ngOnInit() {
    this.getSubjectList();
  }

  getPermissionName(type: PermissionType): string {
    return this.permissionTypes.find(item => item.type === type).name;
  }

  getReadCount(permission: Permission): number {
    return permission.layerPermissions.filter(layerPermission => layerPermission.type === 'V').length;
  }

  getEditCount(permission: Permission): number {
    return permission.layerPermissions.filter(layerPermission => layerPermission.type === 'E').length;
  }

  addPermission() {
    const permission = new Permission({
      group: this.permissionSubject.type === 'group' ? `/auth/api/v1/group/${this.permissionSubject.id}/` : null,
      user: this.permissionSubject.type === 'user' ? `/auth/api/v1/user/${this.permissionSubject.id}/` : null,
      name: this.permissionSubject.name,
      type: this.permissionType.type,
      members: this.permissionSubject.members
    });
    this.project.subjectPermissions.push(permission);

    this.permissionSubject = null;
    this.permissionType = null;
  }

  changePermissionType(permission: Permission, permissionTypeItem: PermissionTypeItem) {
    permission.type = permissionTypeItem.type;
    if (permission.type === 'P') {
      this.showLayers = true;
    }
  }

  deletePermission(permission: Permission) {
    if (permission.id !== undefined) {
      this.project.deletedPermissions.push(`/api/v1/projectpermission/${permission.id}/`);
    }
    const idx = this.project.subjectPermissions.findIndex(item => item === permission);
    this.project.subjectPermissions.splice(idx, 1);
  }

  hideLayers() {
    this.showLayers = false;
    this.layerFilter = null;
    this.filteredLayers = [];
  }

  addLayerPermission(permission: Permission, layer: Layer) {
    permission.layerPermissions.push(
      new LayerPermission({
        project_permission: permission.resource_uri,
        layer_id: (layer as any).layer_id,
        layer: layer.resource_uri,
        layer_name: layer.title,
        type: 'V'
      })
    );
    this.layerFilter = null;
  }

  removeLayerPermission(permission: Permission, layer: any) {
    this.project.deletedLayerPermissions.push({
      project_permission: permission.resource_uri,
      layer_id: layer.layer_id,
      layer: layer.resource_uri,
      layer_name: layer.title,
      type: 'V'
    });
    const idx = permission.layerPermissions.findIndex(item => item === layer);
    permission.layerPermissions.splice(idx, 1);
  }

  filterLayers() {
    if (!this.layerFilter.trim()) {
      return;
    }
    const filterQuery = this.layerFilter.toLowerCase();
    const originalLayers: Layer[] = cloneDeep(this.project.layers);
    const filteredLayers = originalLayers.filter(function recFilter(layer): boolean {
      if (layer.title.toLowerCase().includes(filterQuery)) {
        return true;
      }
      if (layer.sublayers) {
        return !!(layer.sublayers = layer.sublayers.filter(recFilter)).length;
      }
    });
    this.filteredLayers = filteredLayers.length ? filteredLayers : this.project.layers;
  }

  private getSubjectList() {
    return forkJoin(this.permissionService.getUsers(), this.permissionService.getGroups())
      .pipe(map(data => [...data[0], ...data[1]]))
      .subscribe(results => {
        this.autocomplete = results;
      });
  }
}
